<script setup lang="ts">
import { watch } from 'vue'

import LtuSubjectModal from '@/components/current-student-advanced-standing-form/modals/subject-request/LtuSubjectModal.vue'
import SubjectSelectedConfirmModal from '@/components/current-student-advanced-standing-form/modals/subject-request/SubjectSelectedConfirmModal.vue'
import { useSubjectRequestModalController } from '@/composables/current-student-advanced-standing-form/useSubjectRequestModalController'
import {
  buildSubjectRequestPayload,
  externalCourseRequestRemovalMessage,
  externalCourseRequestRemovalPayload,
  externalSubjectRequestRemovalMessage,
  externalSubjectRequestRemovalPayload,
  shouldShowUnspecifiedCreditWarning as shouldShowLegacyUnspecifiedCreditWarning,
  workExperienceSubjectRequestRemovalMessage,
  workExperienceSubjectRequestRemovalPayload,
} from '@/models/current-student-advanced-standing-form/subjectRequests'
import {
  clearModalAlert,
  showModalError,
  showModalInfo,
} from '@/models/current-student-advanced-standing-form/modalAlert'
import type {
  ExternalCourse,
  ExternalSubject,
  StudentEditSubmissionRequest,
  StudentEditSubmissionResponse,
  StudentFormSubmission,
  SubjectRequest,
  SubjectRequestEditPayload,
  SubjectSearchItem,
  SubjectSearchQuery,
  SubjectSearchResponse,
  WorkExperience,
} from '@/models/current-student-advanced-standing-form/api'

type ConfirmDeleteAction = () => Promise<void>

const props = defineProps<{
  externalCourses: ExternalCourse[]
  electiveList: SubjectSearchItem[]
  secureData: string
  modalIsShown: (modalId: string) => boolean
  modalIsActive: (modalId: string) => boolean
  syncModalVisibility: (modalId: string, visible: boolean) => void
  searchSubjects: (query: SubjectSearchQuery) => Promise<SubjectSearchResponse>
  editStudentSubmission: (payload: StudentEditSubmissionRequest) => Promise<StudentEditSubmissionResponse>
  setSubmission: (submission: StudentFormSubmission) => void
  handleAuthSessionError: (error: unknown, behavior: 'redirect' | 'reload') => boolean
  getErrorMessage: (error: unknown) => string
  openConfirmDelete: (message: string, action: ConfirmDeleteAction) => void
}>()

const emit = defineEmits<{
  continuePastUnspecifiedWarning: []
}>()

const {
  ltuSubjectModalVisible,
  ltuSubjectSaving,
  ltuSubjectModalError,
  ltuSubjectModalAlertTone,
  currentSubjectRequestTarget,
  ltuSubjectSearchText,
  ltuSubjectElectiveCode,
  subjectSelectedConfirmModalVisible,
  unspecifiedSubjectsWarningEnable,
  ltuSubjectSuggestions,
  ltuSubjectActiveIndex,
  ltuSubjectElectiveOptions,
  openCourseSubjectRequestModal,
  openSubjectRequestModal,
  openWorkExpSubjectRequestModal,
  closeLtuSubjectModal,
  onLtuSubjectSearchInput,
  selectLtuSubject,
  blurLtuSubjectSearch,
  onLtuSubjectSearchKeydown,
  onLtuSubjectElectiveChange,
} = useSubjectRequestModalController({
  externalCourses: () => props.externalCourses,
  electiveList: () => props.electiveList,
  searchSubjects: props.searchSubjects,
})

watch(ltuSubjectModalVisible, (visible) => props.syncModalVisibility('LtuSubjectModal', visible))
watch(subjectSelectedConfirmModalVisible, (visible) => props.syncModalVisibility('SubjectSelectedConfirmModal', visible))

async function saveLtuSubjectModal(): Promise<void> {
  if (ltuSubjectSaving.value || currentSubjectRequestTarget.value == null) return

  let subjectRequest: SubjectRequestEditPayload | null = null
  if (currentSubjectRequestTarget.value.subject_code == null) {
    if (ltuSubjectElectiveCode.value === '') {
      showModalError(ltuSubjectModalError, ltuSubjectModalAlertTone, 'Please enter a subject')
      return
    }
    subjectRequest = buildSubjectRequestPayload(currentSubjectRequestTarget.value, ltuSubjectElectiveCode.value)
  } else {
    subjectRequest = buildSubjectRequestPayload(currentSubjectRequestTarget.value, currentSubjectRequestTarget.value.subject_code)
  }

  if (subjectRequest == null) return

  ltuSubjectSaving.value = true
  showModalInfo(ltuSubjectModalError, ltuSubjectModalAlertTone, 'Saving...')

  try {
    const result = await props.editStudentSubmission({
      secure_data: props.secureData,
      subject_request_additions: [subjectRequest],
    })
    props.setSubmission(result.data.submission)
    clearModalAlert(ltuSubjectModalError, ltuSubjectModalAlertTone)
    closeLtuSubjectModal()
  } catch (error) {
    if (props.handleAuthSessionError(error, 'reload')) return

    showModalError(ltuSubjectModalError, ltuSubjectModalAlertTone, props.getErrorMessage(error))
  } finally {
    ltuSubjectSaving.value = false
  }
}

function confirmExternalSubjectRequestRemoval(subject: ExternalSubject, request: SubjectRequest): void {
  confirmSubjectRequestRemoval(
    externalSubjectRequestRemovalPayload(subject, request),
    externalSubjectRequestRemovalMessage,
  )
}

function confirmExternalCourseRequestRemoval(course: ExternalCourse, request: SubjectRequest): void {
  confirmSubjectRequestRemoval(
    externalCourseRequestRemovalPayload(course, request),
    externalCourseRequestRemovalMessage,
  )
}

function confirmWorkExperienceSubjectRequestRemoval(workExperience: WorkExperience, request: SubjectRequest): void {
  confirmSubjectRequestRemoval(
    workExperienceSubjectRequestRemovalPayload(workExperience, request),
    workExperienceSubjectRequestRemovalMessage,
  )
}

function confirmSubjectRequestRemoval(payload: SubjectRequestEditPayload, message: string): void {
  props.openConfirmDelete(message, async () => {
    const result = await props.editStudentSubmission({
      secure_data: props.secureData,
      subject_request_removals: [payload],
    })
    props.setSubmission(result.data.submission)
  })
}

function shouldShowUnspecifiedCreditWarning(unspecifiedCreditDetected: boolean): boolean {
  return shouldShowLegacyUnspecifiedCreditWarning(unspecifiedSubjectsWarningEnable.value, unspecifiedCreditDetected)
}

function openSubjectSelectedConfirmModal(): void {
  subjectSelectedConfirmModalVisible.value = true
}

function closeSubjectSelectedConfirmModal(): void {
  subjectSelectedConfirmModalVisible.value = false
}

function continuePastUnspecifiedWarning(): void {
  unspecifiedSubjectsWarningEnable.value = false
  subjectSelectedConfirmModalVisible.value = false
  emit('continuePastUnspecifiedWarning')
}

defineExpose({
  openSubjectRequestModal,
  openCourseSubjectRequestModal,
  openWorkExpSubjectRequestModal,
  confirmExternalSubjectRequestRemoval,
  confirmExternalCourseRequestRemoval,
  confirmWorkExperienceSubjectRequestRemoval,
  closeLtuSubjectModal,
  closeSubjectSelectedConfirmModal,
  shouldShowUnspecifiedCreditWarning,
  openSubjectSelectedConfirmModal,
})
</script>

<template>
  <SubjectSelectedConfirmModal
    :shown="modalIsShown('SubjectSelectedConfirmModal')"
    :active="modalIsActive('SubjectSelectedConfirmModal')"
    @close="closeSubjectSelectedConfirmModal"
    @continue="continuePastUnspecifiedWarning"
  />

  <LtuSubjectModal
    :shown="modalIsShown('LtuSubjectModal')"
    :active="modalIsActive('LtuSubjectModal')"
    v-model:search-text="ltuSubjectSearchText"
    v-model:elective-code="ltuSubjectElectiveCode"
    :suggestions="ltuSubjectSuggestions"
    :active-suggestion-index="ltuSubjectActiveIndex"
    :elective-options="ltuSubjectElectiveOptions"
    :error-message="ltuSubjectModalError"
    :alert-tone="ltuSubjectModalAlertTone"
    :saving="ltuSubjectSaving"
    @search-input="onLtuSubjectSearchInput"
    @search-keydown="onLtuSubjectSearchKeydown"
    @search-blur="blurLtuSubjectSearch"
    @activate-suggestion="ltuSubjectActiveIndex = $event"
    @select-subject="selectLtuSubject"
    @elective-change="onLtuSubjectElectiveChange"
    @close="closeLtuSubjectModal"
    @save="saveLtuSubjectModal"
  />
</template>
